import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { Image } from '../../../components';
import { PatientForm } from '../../../sections';
import { DoctorsService, PatientsService, ToastService } from '../../../services';
import { CreateDoctorPatientDto } from '../../../resources/dtos';
import { GENDER } from '../../../resources/enums';
import { DoctorModel } from '../../../resources/models';

export const RegisterByQR = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [success, setSuccess] = useState(false);
  const [doctor, setDoctor] = useState<DoctorModel | null>(null);
  const [doctorLoadFailed, setDoctorLoadFailed] = useState(false);

  const doctorId = searchParams.get('doctorId') ?? '';
  const hasInsuranceParam = searchParams.get('hasInsurance');
  const defaultHasInsurance = hasInsuranceParam === 'true';

  const isValidLink = useMemo(() => Boolean(doctorId), [doctorId]);

  useEffect(() => {
    if (!doctorId) return;
    setDoctorLoadFailed(false);
    DoctorsService.findPublic(doctorId, false)
      .then((d) => {
        setDoctor(d);
        setDoctorLoadFailed(false);
      })
      .catch(() => {
        setDoctor(null);
        setDoctorLoadFailed(true);
      });
  }, [doctorId]);

  const onSubmit = async (formData: Record<string, unknown>) => {
    const payload: CreateDoctorPatientDto = {
      name: formData.name as string,
      email: formData.email as string,
      birth: formData.birth as number,
      gender: formData.gender as GENDER,
      doctorId,
      doctorName: doctor?.name ?? '',
      hasInsurance: defaultHasInsurance,
    };
    if (defaultHasInsurance) {
      payload.typeFormRefId = uuidv4();
      if (formData.phoneNumber) payload.phoneNumber = formData.phoneNumber as string;
      if (formData.address) payload.address = formData.address as string;
    }
    return PatientsService.createAsDoctor(payload, true)
      .then(() => {
        ToastService.success('toast.invitationSent');
        setSuccess(true);
      })
      .catch((err) => {
        ToastService.showHttpError(err, 'toast.invitePatientFailed');
        throw err;
      });
  };

  if (!isValidLink || doctorLoadFailed) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-6">
        <h1 className="font-medium">{t('registerByQR.invalidLink')}</h1>
        <p className="mt-2 text-center text-gray-dark">{t('registerByQR.invalidLinkDescription')}</p>
      </div>
    );
  }

  if (success) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-6">
        <h1 className="font-medium">{t('registerByQR.successTitle')}</h1>
        <p className="mt-2 text-center text-gray-dark">{t('registerByQR.successDescription')}</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto min-h-screen max-w-140 px-4 py-8">
      <h1 className="font-medium">{t('registerByQR.title')}</h1>
      <p className="mt-2 text-gray-dark">{t('registerByQR.description')}</p>
      {doctor && (
        <div className="mt-3 flex items-center gap-4 rounded-lg border border-stroke3 bg-body2 px-4 py-3">
          <Image
            wrapperClass="h-14 w-14 shrink-0 rounded-full object-cover object-center"
            src={doctor.image?.src}
            placeholder="/assets/images/doctor-avatar.png"
            alt={doctor.name}
          />
          <p className="text-typo1">{t('registerByQR.registeringWith', { name: doctor.name })}</p>
        </div>
      )}
      <div className="mt-8">
        <PatientForm
          defaultHasInsurance={defaultHasInsurance}
          fixedHasInsurance
          onSubmit={onSubmit}
          buttonText={t('registerByQR.register')}
        />
      </div>
    </div>
  );
};
